import { type IVariant } from 'unleash-proxy-client'
import { onMounted, onUnmounted, ref, type Ref } from 'vue'

import { FeatureFlags } from '@/plugins/featureFlags'

// This is really just a thin wrapper to maintain api consistency
// We would rather have users import from `@/utils` than reaching
// into configured plugins via `@/plugins/featureFlags`

/**
 * NOTE: This is not reactive and should not be used to derive computed properties
 * Please use `useReactiveFeatureFlag` if you need reactivity
 *
 * @returns The feature flags plugin
 */
export const useFeatureFlags = (): typeof FeatureFlags => {
  return FeatureFlags
}

/**
 * A composable that provides a reactive reference to the enabled state of a feature flag
 *
 * @param flagName The name of the feature flag to use
 * @returns An object containing a ref to the feature flag's enabled state
 */
export const useReactiveFeatureFlag = (
  flagName: string
): {
  enabled: Ref<boolean>
  variant: Ref<IVariant>
} => {
  const featureFlags = useFeatureFlags()

  const enabled = ref(featureFlags.isEnabled(flagName))
  const variant = ref(featureFlags.getVariant(flagName))

  // reference for cleanup
  const updateFlag = () => {
    const newEnabled = featureFlags.isEnabled(flagName)
    const newVariant = featureFlags.getVariant(flagName)
    // Only update the enabled state if it has changed
    if (newEnabled !== enabled.value) {
      enabled.value = newEnabled
    }
    if (newVariant !== variant.value) {
      variant.value = newVariant
    }
  }

  onMounted(() => {
    featureFlags.on('update', updateFlag)
  })

  onUnmounted(() => {
    featureFlags.off('update', updateFlag)
  })

  return {
    enabled,
    variant,
  }
}
